import React from 'react';

import Type from 'molbio/maps/Type';
import * as TableConstants from 'notebook/tables/TableConstants';
import {AssayResult} from 'results/assays/AssayRecordConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import {FIELD_TYPES, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import * as DateUtil from 'util/DateUtil';

import {SpecialColumn} from './ColumnConstants';

export function getColumnLabelForField(field: SchemaField): string {
  return field.displayName;
}

function _getColumnLabelWidth(label: string): number {
  return new Type(TableConstants.TABLE_AXIS_CELL_FONT_SIZE).width(label.length);
}

export const UUID_COLUMN_WIDTH = 300;
export const DATETIME_COLUMN_WIDTH = 160;
export const DEFAULT_MIN_COLUMN_WIDTH = 118;
const DEFAULT_MIN_HEADER_PADDING = 32;

export function fieldToColumnWidth(field: SchemaField): number {
  // TODO(T10366): wide columns are needed to display UUIDs; when links are displayed properly
  // we can just use default column widths
  switch (field.fieldType) {
    case FIELD_TYPES.ASSAY_REQUEST_LINK.type:
    case FIELD_TYPES.ASSAY_RESULT_LINK.type:
    case FIELD_TYPES.ASSAY_RUN_LINK.type:
      return UUID_COLUMN_WIDTH;
    case FIELD_TYPES.DATETIME.type:
      return DATETIME_COLUMN_WIDTH;
    default: {
      const columnLabel = getColumnLabelForField(field);
      const labelWidth = _getColumnLabelWidth(columnLabel);
      return Math.max(DEFAULT_MIN_COLUMN_WIDTH, labelWidth + DEFAULT_MIN_HEADER_PADDING);
    }
  }
}

// Location of the non-field column - either before other columns or after other columns
export const PRIMARY_METADATA = 'PRIMARY_METADATA';
export const ADDITIONAL_METADATA = 'ADDITIONAL_METADATA';

const ADDITIONAL_COLUMNS: Array<SpecialColumn> = [
  {
    metadataType: ADDITIONAL_METADATA,
    name: `${MODEL_NAMES.Result} ID`,
    width: UUID_COLUMN_WIDTH,
    isHiddenKey: 'hideId',
    renderResult(result: AssayResult): JSX.Element {
      return <span>{this.resultToText(result)}</span>;
    },
    resultToText(result: AssayResult): string {
      return result.id;
    },
  },
  {
    metadataType: ADDITIONAL_METADATA,
    name: 'Creator',
    width: DEFAULT_MIN_COLUMN_WIDTH,
    isHiddenKey: 'hideCreator',
    renderResult(result: AssayResult): JSX.Element {
      return (
        <span>
          <Avatar className="u-mini-spacing-right" size={AvatarSize.XXSMALL} entity={result.creator} />
          {this.resultToText(result)}
        </span>
      );
    },
    resultToText(result: AssayResult): string {
      return result.creator.name;
    },
  },
  {
    metadataType: ADDITIONAL_METADATA,
    name: 'Created At',
    width: DATETIME_COLUMN_WIDTH,
    isHiddenKey: 'hideCreatedAt',
    renderResult(result: AssayResult): JSX.Element {
      return <span>{this.resultToText(result)}</span>;
    },
    resultToText(result: AssayResult): string {
      return DateUtil.readableTime(result.createdAt);
    },
  },
];

export function getSpecialColumns(): Array<SpecialColumn> {
  return ADDITIONAL_COLUMNS;
}
